import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { PLATFORM_META, PlatformBadge, inr } from "../platforms";
import { Trash2, Plus, UploadCloud, Sparkles, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const emptyParty = { name: "", legal_name: "", gstin: "", address1: "", address2: "", city: "", pincode: "", state: "", state_code: "", is_own_branch: false, platform: "" };

const emptyItem = { sku_code: "", platform_sku: "", description: "", hsn: "", upc: "", qty: 1, unit: "SET", rate: 0, discount_pct: 0, gst_rate: 18, cess_rate: 0 };
const STEPS = ["Platform", "Shipment Details", "Parties", "Items", "Review"];
const DRAFT_KEY = "invoicegen_wizard_draft";

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB]";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-600";

export default function NewInvoice() {
  const navigate = useNavigate();
  const { id: editInvoiceId } = useParams();
  const [editInvoiceNo, setEditInvoiceNo] = useState("");
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState(null);
  const [parties, setParties] = useState([]);
  const [skus, setSkus] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef(null);
  const itemsFileRef = useRef(null);
  const [partyOpen, setPartyOpen] = useState(false);
  const [partyField, setPartyField] = useState("bill_to_id");
  const [partyForm, setPartyForm] = useState(emptyParty);

  const saveParty = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/parties", partyForm);
      setParties((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((f) => ({ ...f, [partyField]: data.id }));
      toast.success(`Party "${data.name}" added & selected`);
      setPartyOpen(false);
      setPartyForm(emptyParty);
    } catch (err) { toast.error(apiError(err)); }
  };


  const [form, setForm] = useState({
    platform: "", invoice_date: new Date().toISOString().slice(0, 10),
    platform_fields: {}, transport: {}, bill_to_id: "", ship_to_id: "",
    items: [{ ...emptyItem }], remarks: "", executive: "",
  });
  const [executives, setExecutives] = useState([]);
  const [savedDraft, setSavedDraft] = useState(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    if (editInvoiceId) {
      api.get(`/invoices/${editInvoiceId}`).then((r) => {
        const inv = r.data;
        if (!["draft", "rejected"].includes(inv.status)) { toast.error("Only draft or rejected invoices can be edited"); navigate(`/invoices/${inv.id}`); return; }
        setEditInvoiceNo(inv.invoice_no);
        setForm({
          platform: inv.platform, invoice_date: inv.invoice_date,
          platform_fields: inv.platform_fields || {}, transport: inv.transport || {},
          bill_to_id: inv.bill_to?.id || "", ship_to_id: inv.ship_to?.id || "",
          items: inv.items.map((it) => ({ ...emptyItem, sku_code: it.sku_code || "", platform_sku: it.platform_sku || "", description: it.description, hsn: it.hsn, upc: it.upc || "", qty: it.qty, unit: it.unit, rate: it.rate, discount_pct: it.discount_pct || 0, gst_rate: it.gst_rate, cess_rate: it.cess_rate || 0 })),
          remarks: inv.remarks || "", executive: inv.executive || "",
        });
        setStep(1);
      }).catch(() => navigate("/invoices"));
    } else {
      try {
        const d = JSON.parse(localStorage.getItem(DRAFT_KEY));
        if (d?.form?.platform || d?.form?.items?.some((i) => i.description)) setSavedDraft(d);
      } catch (e) { /* ignore */ }
    }
  }, [editInvoiceId]);

  useEffect(() => {
    if (editInvoiceId) return;
    if (!form.platform && !form.items.some((i) => i.description)) return;
    const t = setTimeout(() => localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step, ts: Date.now() })), 600);
    return () => clearTimeout(t);
  }, [form, step, editInvoiceId]);

  const resumeDraft = () => {
    setForm(savedDraft.form);
    setStep(savedDraft.step || 0);
    setSavedDraft(null);
    restoredRef.current = true;
    toast.success("Draft restored");
  };
  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setSavedDraft(null);
  };

  useEffect(() => {
    api.get("/platforms").then((r) => setConfig(r.data));
    api.get("/parties").then((r) => setParties(r.data));
    api.get("/skus").then((r) => setSkus(r.data));
    api.get("/executives").then((r) => setExecutives(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.platform) api.get("/sku-mappings", { params: { platform: form.platform } }).then((r) => setMappings(r.data));
  }, [form.platform]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setPF = (k, v) => set({ platform_fields: { ...form.platform_fields, [k]: v } });
  const setTR = (k, v) => set({ transport: { ...form.transport, [k]: v } });
  const setItem = (i, patch) => {
    const items = form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    set({ items });
  };

  const normKey = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const findMapping = (code) => {
    const q = normKey(code);
    if (!q) return null;
    let m = mappings.find((x) => normKey(x.platform_sku) === q);
    if (m) return m;
    if (q.length >= 8) {
      const cands = mappings.filter((x) => {
        const k = normKey(x.platform_sku);
        return k.includes(q) || (k.length >= 8 && q.includes(k));
      });
      if (cands.length) return cands.sort((a, b) => normKey(b.platform_sku).length - normKey(a.platform_sku).length)[0];
    }
    const qt = new Set((code || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
    let best = null, bestScore = 0;
    for (const x of mappings) {
      const kt = (x.platform_sku || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
      if (!kt.length) continue;
      const inter = kt.filter((t) => qt.has(t)).length;
      const score = inter / Math.max(qt.size, kt.length);
      if (score > bestScore) { bestScore = score; best = x; }
    }
    return bestScore >= 0.7 ? best : null;
  };

  const pickSku = (i, code) => {
    let sku = skus.find((s) => s.sku_code === code);
    if (!sku && code) {
      const m = findMapping(code);
      if (m) {
        sku = skus.find((s) => s.sku_code === m.master_sku);
        if (sku) toast.success(`Platform SKU "${code}" → Master SKU "${m.master_sku}"`);
      }
    }
    if (sku) setItem(i, { sku_code: sku.sku_code, description: sku.name, hsn: sku.hsn, gst_rate: sku.gst_rate, unit: sku.unit, rate: sku.price || form.items[i].rate, upc: sku.upc || "" });
    else setItem(i, { sku_code: code });
  };

  const pickPlatformSku = (i, code) => {
    const patch = { platform_sku: code };
    const m = code && findMapping(code);
    if (m) {
      const sku = skus.find((s) => s.sku_code === m.master_sku);
      if (sku) {
        const fullName = m.platform_sku.length >= code.trim().length ? m.platform_sku : code.trim();
        Object.assign(patch, { platform_sku: m.platform_sku, sku_code: sku.sku_code, description: fullName, hsn: sku.hsn,
          gst_rate: sku.gst_rate, unit: sku.unit, rate: sku.price || form.items[i].rate, upc: sku.upc || "" });
        toast.success(`"${code}" → Master SKU "${m.master_sku}"`);
      }
    }
    setItem(i, patch);
  };

  const totals = useMemo(() => {
    let taxable = 0, tax = 0;
    for (const it of form.items) {
      const t = (Number(it.qty) || 0) * (Number(it.rate) || 0) * (1 - (Number(it.discount_pct) || 0) / 100);
      taxable += t;
      tax += t * ((Number(it.gst_rate) || 0) + (Number(it.cess_rate) || 0)) / 100;
    }
    return { taxable, tax, grand: Math.round(taxable + tax) };
  }, [form.items]);

  const uploadAI = async (file) => {
    if (!file) return;
    setAiBusy(true);
    const fd = new FormData();
    fd.append("platform", form.platform);
    fd.append("file", file);
    try {
      const { data: job } = await api.post("/ai/extract", fd, { timeout: 60000 });
      let data = null;
      for (let i = 0; i < 150; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: st } = await api.get(`/ai/extract/jobs/${job.job_id}`);
        if (st.status === "done") { data = st.result; break; }
        if (st.status === "error") throw new Error(st.error || "AI extraction failed");
      }
      if (!data) throw new Error("AI extraction timed out — try a smaller file");
      const patch = {};
      if (data.platform_fields) patch.platform_fields = { ...form.platform_fields, ...Object.fromEntries(Object.entries(data.platform_fields).filter(([, v]) => v)) };
      if (data.transport) patch.transport = { ...form.transport, ...Object.fromEntries(Object.entries(data.transport).filter(([, v]) => v)) };
      if (data.invoice_date) patch.invoice_date = data.invoice_date;
      if (data.items?.length) {
        patch.items = data.items.map((it) => ({
          ...emptyItem,
          sku_code: it.sku_code || "", platform_sku: it.platform_sku || "",
          description: it.description || "", hsn: String(it.hsn || ""),
          upc: String(it.upc || ""), qty: Number(it.qty) || 1, unit: it.unit || "SET",
          rate: Number(it.rate) || 0, gst_rate: Number(it.gst_rate) || 18,
        }));
      }
      set(patch);
      const mapped = (data.items || []).filter((i) => i.mapped).length;
      toast.success(`AI extracted ${data.items?.length || 0} items (${mapped} matched to Master SKUs)`, { description: "Review the pre-filled data before proceeding." });
    } catch (e) {
      toast.error("AI extraction failed", { description: apiError(e) });
    } finally {
      setAiBusy(false);
      if (fileRef.current) fileRef.current.value = "";
      if (itemsFileRef.current) itemsFileRef.current.value = "";
    }
  };

  const save = async (submit) => {
    if (!form.executive) {
      toast.error("Executive Name is required — select it in Shipment Details (step 2). Admin can add names in Settings.");
      setStep(1);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({ ...it, qty: Number(it.qty), rate: Number(it.rate), discount_pct: Number(it.discount_pct) || 0, gst_rate: Number(it.gst_rate), cess_rate: Number(it.cess_rate) || 0 })),
      };
      let data;
      if (editInvoiceId) {
        data = (await api.put(`/invoices/${editInvoiceId}`, payload)).data;
      } else {
        data = (await api.post("/invoices", payload)).data;
      }
      if (submit) await api.post(`/invoices/${data.id}/submit`);
      localStorage.removeItem(DRAFT_KEY);
      toast.success(`Invoice ${data.invoice_no} ${submit ? "submitted for approval" : "saved as draft"}`);
      navigate(`/invoices/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!config) return <div className="text-slate-500 text-sm">Loading...</div>;

  const pconf = config.platforms[form.platform];
  const canNext = [
    !!form.platform,
    !!form.executive,
    !!form.bill_to_id && !!form.ship_to_id,
    form.items.length > 0 && form.items.every((i) => i.description && i.hsn && Number(i.qty) > 0 && Number(i.rate) > 0),
    true,
  ][step];

  return (
    <div data-testid="new-invoice-page" className="max-w-6xl">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{editInvoiceId ? "Edit" : "Create"}</p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: "Outfit" }}>
        {editInvoiceId ? `Edit ${editInvoiceNo}` : "New Invoice"}
      </h1>

      {savedDraft && !editInvoiceId && (
        <div data-testid="resume-draft-banner" className="mb-6 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-slate-700">You have an unsaved invoice draft (auto-saved {new Date(savedDraft.ts).toLocaleString()}).</span>
          <button data-testid="resume-draft-btn" onClick={resumeDraft} className="bg-[#0047AB] hover:bg-[#003380] text-white text-xs font-semibold px-4 py-1.5 rounded-full transition-colors duration-200">Resume draft</button>
          <button data-testid="discard-draft-btn" onClick={discardDraft} className="text-xs font-semibold text-slate-500 hover:text-red-600 transition-colors duration-200">Discard</button>
        </div>
      )}

      <div className="flex items-center gap-1 sm:gap-2 mb-8 overflow-x-auto pb-1">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-1 sm:gap-2 shrink-0">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
              i === step ? "bg-[#0047AB] text-white border-[#0047AB]" : i < step ? "bg-blue-50 text-[#0047AB] border-blue-200" : "bg-white text-slate-400 border-slate-200"}`}>
              <span>{i + 1}</span><span className="hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="w-4 h-px bg-slate-300" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(PLATFORM_META).map(([key, m]) => {
            const { Icon } = m;
            const active = form.platform === key;
            return (
              <button key={key} data-testid={`platform-card-${key}`}
                onClick={() => set({ platform: key, platform_fields: {} })}
                className={`bg-white rounded-lg p-6 flex flex-col items-center gap-3 border-2 transition-colors duration-200 hover:border-[#0047AB] ${active ? "border-[#0047AB] ring-4 ring-[#0047AB]/10" : "border-slate-200"}`}>
                <span className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: m.bg, color: m.fg }}>
                  <Icon size={28} />
                </span>
                <span className="font-semibold text-slate-900">{m.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {step === 1 && pconf && (
        <div className="space-y-6">
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 bg-white flex flex-col sm:flex-row items-center gap-4">
            <span className="w-12 h-12 rounded-lg bg-blue-50 text-[#0047AB] flex items-center justify-center shrink-0">
              {aiBusy ? <Loader2 size={24} className="animate-spin" /> : <Sparkles size={24} />}
            </span>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-semibold text-slate-900">AI Auto-fill {form.platform === "blinkit" ? "— upload Blinkit RO" : ""}</div>
              <div className="text-sm text-slate-500">Upload a PDF or JPG ({pconf.name} PO / RO / appointment doc). Claude AI will read it and fill this form including line items.</div>
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
              data-testid="ai-file-input" onChange={(e) => uploadAI(e.target.files[0])} />
            <button data-testid="ai-upload-btn" disabled={aiBusy} onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 disabled:opacity-60">
              <UploadCloud size={15} /> {aiBusy ? "Reading document..." : "Upload document"}
            </button>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <PlatformBadge platform={form.platform} size={15} />
              <h2 className="font-semibold text-slate-900">{pconf.name} Details</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Invoice Date *</label>
                <input data-testid="invoice-date-input" type="date" value={form.invoice_date} onChange={(e) => set({ invoice_date: e.target.value })} className={`${inputCls} mt-1`} />
              </div>
              <div>
                <label className={labelCls}>Executive Name *</label>
                <select data-testid="executive-select" value={form.executive} onChange={(e) => set({ executive: e.target.value })} className={`${inputCls} mt-1`}>
                  <option value="">— Select executive —</option>
                  {executives.map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
                  {form.executive && !executives.some((x) => x.name === form.executive) && <option value={form.executive}>{form.executive}</option>}
                </select>
                {executives.length === 0 && <p className="text-xs text-amber-600 mt-1">No executives yet — Admin can add them in Settings.</p>}
              </div>
              {pconf.fields.map((f) => (
                <div key={f.key}>
                  <label className={labelCls}>{f.label}{f.required ? " *" : ""}</label>
                  <input data-testid={`pf-${f.key}-input`} value={form.platform_fields[f.key] || ""} onChange={(e) => setPF(f.key, e.target.value)} className={`${inputCls} mt-1 font-mono`} />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Transport / E-Way Bill Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {config.transport_fields.map((f) => (
                <div key={f.key}>
                  <label className={labelCls}>{f.label}</label>
                  {f.key === "transport_mode" ? (
                    <select data-testid={`tr-${f.key}-input`} value={String(form.transport[f.key] || "1")}
                      onChange={(e) => setTR(f.key, e.target.value)} className={`${inputCls} mt-1`}>
                      <option value="1">Road</option>
                      <option value="2">Rail</option>
                      <option value="3">Air</option>
                      <option value="4">Ship</option>
                    </select>
                  ) : (
                  <input data-testid={`tr-${f.key}-input`} type={f.key === "lr_date" ? "date" : "text"}
                    value={form.transport[f.key] || ""} onChange={(e) => setTR(f.key, e.target.value)} className={`${inputCls} mt-1`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid lg:grid-cols-2 gap-6">
          {[["bill_to_id", "Bill To"], ["ship_to_id", "Ship To (Consignee)"]].map(([field, title]) => (
            <div key={field} className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-slate-900">{title}</h2>
                <button data-testid={`${field}-add-party-btn`} onClick={() => { setPartyField(field); setPartyForm(emptyParty); setPartyOpen(true); }}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#0047AB] hover:underline">
                  <Plus size={13} /> Add Party
                </button>
              </div>
              <div data-testid={`${field}-list`} className="space-y-2 max-h-96 overflow-y-auto">
                {parties.map((p) => (
                  <button key={p.id} data-testid={`${field}-option-${p.id}`}
                    onClick={() => set({ [field]: p.id })}
                    className={`w-full text-left border rounded-lg p-3 transition-colors duration-200 ${form[field] === p.id ? "border-[#0047AB] bg-blue-50" : "border-slate-200 hover:border-slate-400"}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{p.name}</span>
                      {p.is_own_branch && <span className="text-[10px] uppercase tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5 font-semibold">Own Branch</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.city}, {p.state} {p.gstin && <span className="font-mono">· {p.gstin}</span>}</div>
                  </button>
                ))}
                {parties.length === 0 && <div className="text-sm text-slate-400">No parties yet — add them in Address Book.</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 bg-white flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-blue-50 text-[#0047AB] flex items-center justify-center shrink-0">
              {aiBusy ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
            </span>
            <span className="text-sm text-slate-500 flex-1">Upload a screenshot / photo / PDF of the item list — AI fills the rows below.</span>
            <input ref={itemsFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
              data-testid="items-ai-file-input" onChange={(e) => uploadAI(e.target.files[0])} />
            <button data-testid="items-ai-upload-btn" disabled={aiBusy} onClick={() => itemsFileRef.current?.click()}
              className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-200 disabled:opacity-60">
              <UploadCloud size={14} /> {aiBusy ? "Reading..." : "Upload screenshot"}
            </button>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[1250px]">
              <thead>
                <tr className="bg-[#0A192F] text-white text-left">
                  {[`${PLATFORM_META[form.platform]?.name || "Platform"} SKU`, "Master SKU", "Description", "HSN", "Qty", "Unit", "Rate", "Disc %", "GST %", "Amount", ""].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {form.items.map((it, i) => (
                  <tr key={i}>
                    <td className="px-2 py-2 w-48">
                      <input list="psku-list" data-testid={`item-${i}-psku-input`} value={it.platform_sku}
                        onChange={(e) => pickPlatformSku(i, e.target.value)} placeholder={`${PLATFORM_META[form.platform]?.name || ""} SKU...`}
                        className={`${inputCls} font-mono text-xs min-w-40`} />
                    </td>
                    <td className="px-2 py-2 w-52">
                      <input list="sku-list" data-testid={`item-${i}-sku-input`} value={it.sku_code}
                        onChange={(e) => pickSku(i, e.target.value)} placeholder="Master SKU..."
                        className={`${inputCls} font-mono text-xs min-w-44`} />
                    </td>
                    <td className="px-2 py-2 min-w-60"><input data-testid={`item-${i}-desc-input`} value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} className={inputCls} /></td>
                    <td className="px-2 py-2 w-32"><input data-testid={`item-${i}-hsn-input`} value={it.hsn} onChange={(e) => setItem(i, { hsn: e.target.value })} className={`${inputCls} font-mono min-w-24`} /></td>
                    <td className="px-2 py-2 w-28"><input data-testid={`item-${i}-qty-input`} type="number" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className={`${inputCls} text-right min-w-20`} /></td>
                    <td className="px-2 py-2 w-24"><input value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })} className={`${inputCls} min-w-16`} /></td>
                    <td className="px-2 py-2 w-36"><input data-testid={`item-${i}-rate-input`} type="number" step="0.01" value={it.rate} onChange={(e) => setItem(i, { rate: e.target.value })} className={`${inputCls} text-right min-w-28`} /></td>
                    <td className="px-2 py-2 w-24"><input type="number" value={it.discount_pct} onChange={(e) => setItem(i, { discount_pct: e.target.value })} className={`${inputCls} text-right min-w-16`} /></td>
                    <td className="px-2 py-2 w-24"><input data-testid={`item-${i}-gst-input`} type="number" value={it.gst_rate} onChange={(e) => setItem(i, { gst_rate: e.target.value })} className={`${inputCls} text-right min-w-16`} /></td>
                    <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{inr((Number(it.qty) || 0) * (Number(it.rate) || 0) * (1 - (Number(it.discount_pct) || 0) / 100))}</td>
                    <td className="px-2 py-2">
                      <button data-testid={`item-${i}-remove-btn`} onClick={() => set({ items: form.items.filter((_, x) => x !== i) })} className="text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <datalist id="sku-list">
              {skus.map((s) => <option key={s.id} value={s.sku_code} label={s.name} />)}
            </datalist>
            <datalist id="psku-list">
              {mappings.map((m) => <option key={m.id} value={m.platform_sku} label={`→ ${m.master_sku}`} />)}
            </datalist>
          </div>
          <button data-testid="add-item-btn" onClick={() => set({ items: [...form.items, { ...emptyItem }] })}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0047AB] hover:underline">
            <Plus size={15} /> Add item
          </button>
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-6 justify-end text-sm">
            <div>Taxable: <span className="font-mono font-semibold">{inr(totals.taxable)}</span></div>
            <div>Tax: <span className="font-mono font-semibold">{inr(totals.tax)}</span></div>
            <div className="text-base">Total: <span data-testid="items-grand-total" className="font-mono font-bold text-slate-900">{inr(totals.grand)}</span></div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 space-y-4">
            <div className="flex items-center gap-3">
              <PlatformBadge platform={form.platform} />
              <div>
                <div className="font-semibold text-slate-900">{PLATFORM_META[form.platform]?.name} shipment invoice</div>
                <div className="text-xs text-slate-500">Dated {form.invoice_date} · Invoice number auto-assigned on save</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className={labelCls}>Bill To</div>
                <div className="mt-1 font-medium">{parties.find((p) => p.id === form.bill_to_id)?.name}</div>
              </div>
              <div>
                <div className={labelCls}>Ship To</div>
                <div className="mt-1 font-medium">{parties.find((p) => p.id === form.ship_to_id)?.name}</div>
              </div>
            </div>
            <div className="text-sm">
              <div className={labelCls}>Platform fields</div>
              <div className="mt-1 font-mono text-xs text-slate-600">
                {Object.entries(form.platform_fields).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" | ") || "—"}
              </div>
            </div>
            <div className="text-sm border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className={labelCls}>Items — quick edit qty &amp; rate</div>
                <div>Grand Total: <span className="font-mono font-bold">{inr(totals.grand)}</span></div>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-6 sm:col-span-7 min-w-0">
                      <div className="truncate font-medium text-slate-900">{it.description || "—"}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{it.sku_code} · HSN {it.hsn} · GST {it.gst_rate}%</div>
                    </div>
                    <input data-testid={`review-item-${i}-qty`} type="number" value={it.qty}
                      onChange={(e) => setItem(i, { qty: e.target.value })}
                      className={`${inputCls} col-span-2 text-right`} />
                    <input data-testid={`review-item-${i}-rate`} type="number" step="0.01" value={it.rate}
                      onChange={(e) => setItem(i, { rate: e.target.value })}
                      className={`${inputCls} col-span-2 text-right`} />
                    <div className="col-span-2 sm:col-span-1 text-right font-mono text-xs whitespace-nowrap">
                      {inr((Number(it.qty) || 0) * (Number(it.rate) || 0) * (1 - (Number(it.discount_pct) || 0) / 100))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Narration</label>
              <textarea data-testid="narration-input" placeholder="Narration to be printed on the invoice" value={form.remarks} onChange={(e) => set({ remarks: e.target.value })} rows={2} className={`${inputCls} mt-1`} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 justify-end">
            <button data-testid="save-draft-btn" disabled={busy} onClick={() => save(false)}
              className="border border-slate-300 hover:border-slate-500 text-slate-700 text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 disabled:opacity-60">
              Save as Draft
            </button>
            <button data-testid="save-submit-btn" disabled={busy} onClick={() => save(true)}
              className="bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 disabled:opacity-60">
              {busy ? "Saving..." : "Save & Submit for Approval"}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between mt-8">
        <button data-testid="wizard-back-btn" disabled={step === 0} onClick={() => setStep(step - 1)}
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors duration-200 disabled:opacity-30">
          <ChevronLeft size={16} /> Back
        </button>
        {step < STEPS.length - 1 && (
          <button data-testid="wizard-next-btn" disabled={!canNext} onClick={() => setStep(step + 1)}
            className="inline-flex items-center gap-1 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors duration-200 disabled:opacity-40">
            Next <ChevronRight size={16} />
          </button>
        )}
      </div>

      <Dialog open={partyOpen} onOpenChange={setPartyOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Party ({partyField === "bill_to_id" ? "Bill To" : "Ship To"})</DialogTitle></DialogHeader>
          <form onSubmit={saveParty} className="grid sm:grid-cols-2 gap-4">
            {[["name", "Display Name *", true], ["legal_name", "Legal Name", false], ["gstin", "GSTIN", false],
              ["address1", "Address Line 1 *", true], ["address2", "Address Line 2", false], ["city", "City *", true],
              ["pincode", "Pincode *", true], ["state", "State *", true], ["state_code", "State Code (e.g. 09) *", true]].map(([k, label, req]) => (
              <div key={k} className={k.startsWith("address") ? "sm:col-span-2" : ""}>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</label>
                <input data-testid={`wizard-party-${k}-input`} required={req} value={partyForm[k]}
                  onChange={(e) => setPartyForm({ ...partyForm, [k]: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB]" />
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="wizard-party-own-branch-checkbox" type="checkbox" checked={partyForm.is_own_branch}
                onChange={(e) => setPartyForm({ ...partyForm, is_own_branch: e.target.checked })} />
              Own Branch (Bill-to entity)
            </label>
            <div className="flex justify-end">
              <button data-testid="wizard-party-save-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">Save &amp; Select</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
